import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, API } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { 
  Plus, FileText, CheckCircle2, MessageSquare, AlertCircle, HelpCircle,
  Clock, User, ChevronDown, Check, UserPlus, Bot, Copy, Sparkles,
  X, Phone, Calendar, Home, Hash, Globe, Send, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import axios from 'axios';
import { toast } from 'sonner';
import { trackModuleVisit, trackEvent } from '@/lib/posthog';

// ── Helpers ──

const TYPE_CONFIGS = {
  question: { label: 'Pregunta', icon: HelpCircle, color: 'text-blue-500', bg: 'bg-blue-50' },
  complaint: { label: 'Queja', icon: AlertCircle, color: 'text-orange-500', bg: 'bg-orange-50' },
  claim: { label: 'Reclamo', icon: AlertCircle, color: 'text-red-500', bg: 'bg-red-50' },
  suggestion: { label: 'Sugerencia', icon: MessageSquare, color: 'text-green-500', bg: 'bg-green-50' }
};

const STATUS_CONFIGS = {
  pending: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: Clock },
  in_progress: { label: 'En progreso', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Clock },
  resolved: { label: 'Resuelto', color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle2 },
  closed: { label: 'Cerrado', color: 'bg-slate-100 text-slate-700 border-slate-200', icon: Check }
};

const URGENCY_CONFIGS = {
  alta: { label: 'Urgente', color: 'bg-red-100 text-red-700 border border-red-200' },
  media: { label: 'Media', color: 'bg-yellow-100 text-yellow-700 border border-yellow-200' },
  baja: { label: 'Baja', color: 'bg-slate-100 text-slate-600 border border-slate-200' }
};

const PRIORITY_CONFIGS = {
  low: { label: 'Baja', color: 'text-slate-500 bg-slate-50' },
  medium: { label: 'Media', color: 'text-blue-600 bg-blue-50' },
  high: { label: 'Alta', color: 'text-orange-600 bg-orange-50' },
  critical: { label: 'Crítica', color: 'text-red-600 bg-red-50' }
};

const CATEGORY_LABELS = {
  mantenimiento: 'Mantenimiento',
  seguridad: 'Seguridad',
  aseo: 'Aseo',
  financiero: 'Financiero',
  convivencia: 'Convivencia',
  consulta: 'Consulta',
  otro: 'Otro'
};

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function fallbackCopy(text) {
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.position = 'fixed';
  ta.style.left = '-9999px';
  document.body.appendChild(ta);
  ta.select();
  document.execCommand('copy');
  document.body.removeChild(ta);
}

function copyToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).then(
      () => toast.success('Copiado al portapapeles'),
      () => { fallbackCopy(text); toast.success('Copiado al portapapeles'); }
    );
  } else {
    fallbackCopy(text);
    toast.success('Copiado al portapapeles');
  }
}

// ── Slide-Over Detail Panel ──

function PQRSDetailPanel({ pqrs, isAdmin, onClose, onStatusChange, onAssign, staffUsers, onPqrsUpdated }) {
  const typeConfig = TYPE_CONFIGS[pqrs.type] || { label: pqrs.type, icon: FileText, color: 'text-slate-500', bg: 'bg-slate-50' };
  const statusConfig = STATUS_CONFIGS[pqrs.status] || STATUS_CONFIGS.pending;
  const TypeIcon = typeConfig.icon;

  // WhatsApp reply state
  const [waModalOpen, setWaModalOpen] = useState(false);
  const [waMessage, setWaMessage] = useState('');
  const [waSendState, setWaSendState] = useState('idle'); // idle | sending | sent | error

  // Conversation history (WhatsApp)
  const [messages, setMessages] = useState(null);
  const fetchMessages = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/pqrs/${pqrs.id}/messages`);
      setMessages(res.data);
    } catch {
      setMessages([]);
    }
  }, [pqrs.id]);
  useEffect(() => { setMessages(null); fetchMessages(); }, [fetchMessages]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') { if (waModalOpen) setWaModalOpen(false); else onClose(); } };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose, waModalOpen]);

  const isWhatsApp = pqrs.title?.toLowerCase().includes('whatsapp') || pqrs.source === 'jelou' || pqrs.source === 'whatsapp_jelou';
  const hasPhone = !!pqrs.telefono_contacto;

  const openWhatsAppModal = () => {
    setWaMessage(pqrs.ai_respuesta_sugerida || '');
    setWaSendState('idle');
    setWaModalOpen(true);
  };

  const sendWhatsApp = async () => {
    if (!waMessage.trim()) return;
    setWaSendState('sending');
    try {
      await axios.post(`${API}/pqrs/${pqrs.id}/whatsapp-reply`, { message: waMessage });
      setWaSendState('sent');
      toast.success('Mensaje enviado por WhatsApp');
      if (onPqrsUpdated) onPqrsUpdated();
      fetchMessages();
      setTimeout(() => { setWaModalOpen(false); setWaSendState('idle'); }, 2000);
    } catch (err) {
      setWaSendState('error');
      const detail = err.response?.data?.detail || 'Error al enviar';
      toast.error(detail);
      setTimeout(() => setWaSendState('idle'), 3000);
    }
  };

  // Extract phone display from description or contact field
  const phoneDisplay = pqrs.telefono_contacto || null;
  // Extract resident name from user_name
  const residentName = pqrs.user_name || 'Sin nombre';

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/30 z-40 animate-fade-in"
        onClick={onClose}
        data-testid="pqrs-detail-overlay"
      />
      {/* Panel */}
      <div
        className="fixed top-0 right-0 h-full w-full sm:w-[440px] bg-white z-50 shadow-2xl flex flex-col animate-slide-in-right"
        data-testid="pqrs-detail-panel"
      >
        {/* ─ HEADER ─ */}
        <div className="bg-slate-50 border-b border-slate-200 px-5 py-4 flex-shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <div className={`h-7 w-7 rounded-lg flex items-center justify-center ${typeConfig.bg} ${typeConfig.color} flex-shrink-0`}>
                  <TypeIcon className="h-3.5 w-3.5" />
                </div>
                <h2 className="text-sm font-bold text-slate-900 truncate" data-testid="detail-title">
                  {pqrs.title}
                </h2>
              </div>
              <p className="text-xs text-slate-400 font-mono" data-testid="detail-id">
                {pqrs.id}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-slate-200 transition-colors flex-shrink-0"
              data-testid="detail-close-btn"
            >
              <X className="h-5 w-5 text-slate-500" />
            </button>
          </div>
          <div className="flex items-center gap-2 mt-3">
            <Badge variant="outline" className={`text-[11px] px-2 py-0.5 ${typeConfig.bg} ${typeConfig.color} border-0 font-medium`}>
              {typeConfig.label}
            </Badge>
            {isAdmin ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className={`${statusConfig.color} font-medium gap-1 h-6 text-[11px] px-2`} data-testid="detail-status-dropdown">
                    <statusConfig.icon className="h-3 w-3" />
                    {statusConfig.label}
                    <ChevronDown className="h-2.5 w-2.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {['pending', 'in_progress'].map(s => (
                    <DropdownMenuItem key={s} onClick={() => onStatusChange(pqrs.id, s)} className="gap-2 text-sm">
                      {React.createElement(STATUS_CONFIGS[s].icon, { className: `h-4 w-4 ${s === 'pending' ? 'text-yellow-500' : 'text-blue-500'}` })}
                      {STATUS_CONFIGS[s].label}
                      {pqrs.status === s && <Check className="h-4 w-4 ml-auto" />}
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => onStatusChange(pqrs.id, 'resolved')} className="gap-2 text-green-600 text-sm">
                    <CheckCircle2 className="h-4 w-4" />
                    Marcar como resuelto
                    {pqrs.status === 'resolved' && <Check className="h-4 w-4 ml-auto" />}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onStatusChange(pqrs.id, 'closed')} className="gap-2 text-sm">
                    <Check className="h-4 w-4 text-slate-500" />
                    Cerrar
                    {pqrs.status === 'closed' && <Check className="h-4 w-4 ml-auto" />}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Badge variant="outline" className={`${statusConfig.color} text-[11px] px-2 py-0.5 font-medium`}>
                {statusConfig.label}
              </Badge>
            )}
          </div>
        </div>

        {/* ─ SCROLLABLE BODY ─ */}
        <div className="flex-1 overflow-y-auto">
          {/* Residente */}
          <div className="px-5 py-4 border-b border-slate-100">
            <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Residente</h4>
            <div className="space-y-2">
              <div className="flex items-center gap-2.5 text-sm text-slate-700">
                <User className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                <span className="font-medium">{residentName}</span>
              </div>
              {pqrs.unit_number && (
                <div className="flex items-center gap-2.5 text-sm text-slate-600">
                  <Home className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                  <span>{pqrs.unit_number}</span>
                </div>
              )}
              {phoneDisplay && (
                <div className="flex items-center gap-2.5 text-sm">
                  <Phone className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                  <a href={`tel:${phoneDisplay}`} className="text-primary hover:underline">{phoneDisplay}</a>
                </div>
              )}
              <div className="flex items-center gap-2.5 text-sm text-slate-600">
                <Calendar className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                <span>{formatDate(pqrs.created_at)}</span>
              </div>
              <div className="flex items-center gap-2.5 text-sm text-slate-600">
                <Globe className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                <span>{isWhatsApp ? 'Via WhatsApp' : 'Manual'}</span>
              </div>
            </div>
          </div>

          {/* Descripción completa */}
          <div className="px-5 py-4 border-b border-slate-100">
            <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Descripcion</h4>
            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap" data-testid="detail-description">
              {pqrs.description}
            </p>
          </div>

          {/* Asignado a */}
          {pqrs.assigned_to_name && (
            <div className="px-5 py-3 border-b border-slate-100">
              <div className="flex items-center gap-2 text-sm bg-blue-50 text-blue-700 px-3 py-2 rounded-lg">
                <User className="h-3.5 w-3.5" />
                Asignado a: <strong>{pqrs.assigned_to_name}</strong>
              </div>
            </div>
          )}

          {/* OpsAgent IA */}
          {pqrs.ai_procesado && (
            <div className="px-5 py-4 border-b border-slate-100">
              <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <Bot className="h-3.5 w-3.5 text-primary" />
                OpsAgent IA
              </h4>
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                {pqrs.ai_urgencia && (
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${URGENCY_CONFIGS[pqrs.ai_urgencia]?.color || 'bg-slate-100 text-slate-600'}`}>
                    {URGENCY_CONFIGS[pqrs.ai_urgencia]?.label || pqrs.ai_urgencia}
                  </span>
                )}
                {pqrs.ai_categoria && (
                  <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                    {CATEGORY_LABELS[pqrs.ai_categoria] || pqrs.ai_categoria}
                  </span>
                )}
                {pqrs.ai_confianza && (
                  <span className="text-[11px] text-slate-400 ml-auto">
                    {Math.round(pqrs.ai_confianza * 100)}% confianza
                  </span>
                )}
              </div>
              {pqrs.ai_resumen && (
                <p className="text-sm text-slate-600 mb-3">{pqrs.ai_resumen}</p>
              )}
              {pqrs.ai_respuesta_sugerida && (
                <>
                  <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                    <p className="text-sm text-slate-700 leading-relaxed" data-testid="detail-ai-response">
                      {pqrs.ai_respuesta_sugerida}
                    </p>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(pqrs.ai_respuesta_sugerida)}
                      className="flex-1 h-8 text-xs gap-1.5"
                      data-testid="detail-copy-response"
                    >
                      <Copy className="h-3 w-3" />
                      Copiar respuesta
                    </Button>
                    {hasPhone && isAdmin && !pqrs.respuesta_enviada_whatsapp && (
                      <Button
                        size="sm"
                        onClick={openWhatsAppModal}
                        className="flex-1 h-8 text-xs gap-1.5 bg-green-600 hover:bg-green-700 text-white"
                        data-testid="detail-send-whatsapp"
                      >
                        <Send className="h-3 w-3" />
                        Enviar por WhatsApp
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Conversación WhatsApp */}
          <div className="px-5 py-4 border-b border-slate-100">
            <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <MessageSquare className="h-3.5 w-3.5 text-green-600" />
              Conversacion
            </h4>
            {messages === null ? (
              <div className="space-y-2" data-testid="conversation-skeleton">
                <div className="h-10 w-3/4 bg-slate-100 rounded-xl animate-pulse" />
                <div className="h-10 w-2/3 bg-slate-100 rounded-xl animate-pulse ml-auto" />
              </div>
            ) : messages.length === 0 ? (
              <p className="text-sm text-slate-400" data-testid="conversation-empty">
                {isWhatsApp
                  ? 'Sin mensajes registrados para este ticket.'
                  : 'Este PQRS fue creado manualmente, no tiene conversación de WhatsApp.'}
              </p>
            ) : (
              <div className="space-y-2" data-testid="conversation-timeline">
                {messages.map((msg) => (
                  <div key={msg.id} className={`flex ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-[85%] rounded-xl px-3 py-2 border ${msg.direction === 'outbound' ? 'bg-green-50 border-green-100' : 'bg-slate-50 border-slate-100'}`}
                      data-testid={`conversation-msg-${msg.id}`}
                    >
                      <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                      <p className={`text-[10px] mt-1 ${msg.direction === 'outbound' ? 'text-green-600' : 'text-slate-400'}`}>
                        {msg.direction === 'outbound' ? 'Admin' : 'Residente'} · {formatDate(msg.created_at)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* WhatsApp reply sent confirmation */}
          {pqrs.respuesta_enviada_whatsapp && (
            <div className="px-5 py-3 border-b border-slate-100">
              <div className="flex items-center gap-2 text-sm bg-green-50 text-green-700 px-3 py-2 rounded-lg">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span>Respuesta enviada {pqrs.fecha_respuesta ? `el ${formatDate(pqrs.fecha_respuesta)}` : 'por WhatsApp'}</span>
              </div>
            </div>
          )}
        </div>

        {/* ─ STICKY ACTIONS ─ */}
        <div className="border-t border-slate-200 bg-white px-5 py-4 flex-shrink-0 space-y-2">
          {pqrs.status === 'resolved' || pqrs.status === 'closed' ? (
            <div className="text-center text-sm text-slate-500">
              <CheckCircle2 className="h-5 w-5 mx-auto mb-1 text-green-500" />
              {pqrs.status === 'resolved' ? 'Resuelto' : 'Cerrado'} {pqrs.resolved_at ? `el ${formatDate(pqrs.resolved_at)}` : ''}
            </div>
          ) : isAdmin && (
            <>
              <Button
                onClick={() => { onStatusChange(pqrs.id, 'resolved'); onClose(); }}
                className="w-full h-9 bg-green-600 hover:bg-green-700 text-white gap-2 text-sm font-semibold"
                data-testid="detail-resolve-btn"
              >
                <CheckCircle2 className="h-4 w-4" />
                Marcar como Resuelto
              </Button>
              <Button
                variant="outline"
                onClick={() => onAssign(pqrs)}
                className="w-full h-9 gap-2 text-sm"
                data-testid="detail-assign-btn"
              >
                <UserPlus className="h-4 w-4" />
                {pqrs.assigned_to ? 'Reasignar' : 'Asignar a...'}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* ─ WhatsApp Confirm Modal ─ */}
      {waModalOpen && (
        <>
          <div className="fixed inset-0 bg-black/40 z-[60]" onClick={() => waSendState === 'idle' && setWaModalOpen(false)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[95vw] max-w-md bg-white rounded-xl shadow-2xl z-[70] overflow-hidden" data-testid="whatsapp-confirm-modal">
            <div className="bg-green-600 px-5 py-3 flex items-center gap-2">
              <Send className="h-4 w-4 text-white" />
              <h3 className="text-sm font-bold text-white">Enviar por WhatsApp</h3>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 px-3 py-2 rounded-lg">
                <Phone className="h-4 w-4 text-green-600" />
                <span>A: <strong>{residentName}</strong> ({phoneDisplay})</span>
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">Mensaje (editable)</Label>
                <Textarea
                  value={waMessage}
                  onChange={(e) => setWaMessage(e.target.value)}
                  rows={5}
                  className="text-sm resize-none border-slate-200"
                  disabled={waSendState !== 'idle'}
                  data-testid="whatsapp-message-textarea"
                />
              </div>
            </div>
            <div className="px-5 pb-5 flex gap-2">
              <Button
                variant="outline"
                onClick={() => setWaModalOpen(false)}
                disabled={waSendState === 'sending'}
                className="flex-1 h-9 text-sm"
              >
                Cancelar
              </Button>
              <Button
                onClick={sendWhatsApp}
                disabled={waSendState !== 'idle' || !waMessage.trim()}
                className={`flex-1 h-9 text-sm font-semibold gap-2 ${
                  waSendState === 'sent' ? 'bg-green-600' :
                  waSendState === 'error' ? 'bg-red-600' :
                  'bg-green-600 hover:bg-green-700'
                } text-white`}
                data-testid="whatsapp-confirm-send"
              >
                {waSendState === 'idle' && <><Send className="h-3.5 w-3.5" />Confirmar envio</>}
                {waSendState === 'sending' && <><Loader2 className="h-3.5 w-3.5 animate-spin" />Enviando...</>}
                {waSendState === 'sent' && <><CheckCircle2 className="h-3.5 w-3.5" />Enviado</>}
                {waSendState === 'error' && <><X className="h-3.5 w-3.5" />Error al enviar</>}
              </Button>
            </div>
          </div>
        </>
      )}
    </>
  );
}


// ── Main Page ──

const PQRSPage = () => {
  const { user, selectedBuilding } = useAuth();
  const [pqrsList, setPqrsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedPqrs, setSelectedPqrs] = useState(null);
  const [detailPqrs, setDetailPqrs] = useState(null);
  const [buildings, setBuildings] = useState([]);
  const [staffUsers, setStaffUsers] = useState([]);
  const [formData, setFormData] = useState({
    building_id: '',
    title: '',
    description: '',
    type: 'question',
    priority: 'medium'
  });

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  useEffect(() => { trackModuleVisit('pqrs'); }, []);

  useEffect(() => {
    fetchBuildings();
    if (isAdmin) fetchStaffUsers();
  }, [isAdmin]);

  useEffect(() => { fetchPQRS(); }, [selectedBuilding]);

  const fetchBuildings = async () => {
    try {
      const response = await axios.get(`${API}/buildings`);
      setBuildings(response.data);
      const defaultBuilding = (selectedBuilding && selectedBuilding !== 'all') 
        ? selectedBuilding 
        : (response.data.length > 0 ? response.data[0].id : '');
      setFormData(prev => ({ ...prev, building_id: defaultBuilding }));
    } catch (error) {
      console.error('Error fetching buildings:', error);
    }
  };

  const fetchStaffUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setStaffUsers(response.data.filter(u => 
        u.role === 'admin' || u.role === 'staff' || u.role === 'super_admin'
      ));
    } catch (error) {
      console.error('Error fetching staff users:', error);
    }
  };

  const fetchPQRS = async () => {
    setLoading(true);
    try {
      const query = selectedBuilding && selectedBuilding !== 'all' ? `?building_id=${selectedBuilding}` : '';
      const response = await axios.get(`${API}/pqrs${query}`);
      setPqrsList(response.data);
    } catch (error) {
      console.error('Error fetching PQRS:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/pqrs`, formData);
      toast.success('PQRS creada exitosamente');
      trackEvent('pqrs_created', { building_id: formData.building_id, categoria: formData.type });
      setDialogOpen(false);
      setFormData({ building_id: buildings[0]?.id || '', title: '', description: '', type: 'question', priority: 'medium' });
      fetchPQRS();
    } catch (error) {
      toast.error('Error al crear PQRS');
    }
  };

  const handleStatusChange = useCallback(async (pqrsId, newStatus) => {
    try {
      const pqrs = pqrsList.find(p => p.id === pqrsId);
      await axios.patch(`${API}/pqrs/${pqrsId}`, { status: newStatus });
      trackEvent('pqrs_status_changed', { building_id: pqrs?.building_id, status_nuevo: newStatus });
      if (newStatus === 'resolved') {
        trackEvent('pqrs_resolved', { building_id: pqrs?.building_id });
        toast.success('PQRS marcada como resuelta');
      } else {
        toast.success('Estado actualizado');
      }
      fetchPQRS();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  }, [pqrsList]);

  const handleAssign = async (pqrsId, userId) => {
    try {
      const pqrs = pqrsList.find(p => p.id === pqrsId);
      await axios.patch(`${API}/pqrs/${pqrsId}`, { assigned_to: userId });
      trackEvent('pqrs_assigned', { building_id: pqrs?.building_id });
      toast.success('PQRS asignada correctamente');
      setAssignDialogOpen(false);
      setSelectedPqrs(null);
      fetchPQRS();
    } catch (error) {
      toast.error('Error al asignar PQRS');
    }
  };

  const openAssignDialog = (pqrs) => {
    setSelectedPqrs(pqrs);
    setAssignDialogOpen(true);
  };

  const closeDetail = useCallback(() => setDetailPqrs(null), []);

  // Update detail when list refreshes
  useEffect(() => {
    if (detailPqrs) {
      const updated = pqrsList.find(p => p.id === detailPqrs.id);
      if (updated) setDetailPqrs(updated);
    }
  }, [pqrsList]);

  // Sort by urgency then date
  const sortedPqrsList = [...pqrsList].sort((a, b) => {
    const urgencyOrder = { alta: 0, media: 1, baja: 2, undefined: 3 };
    const diff = (urgencyOrder[a.ai_urgencia] ?? 3) - (urgencyOrder[b.ai_urgencia] ?? 3);
    return diff !== 0 ? diff : new Date(b.created_at) - new Date(a.created_at);
  });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-heading font-bold text-slate-900" data-testid="pqrs-page-title">PQRS</h1>
            <p className="text-slate-500 mt-1 text-lg">Gestión de solicitudes y requerimientos</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary/90 text-white rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 px-6 h-11" data-testid="create-pqrs-button">
                <Plus className="h-5 w-5 mr-2" />
                Nueva Solicitud
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px]" data-testid="create-pqrs-dialog">
              <DialogHeader>
                <DialogTitle className="text-xl font-heading font-bold text-slate-900">Crear nueva PQRS</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-5 py-4">
                <div className="grid gap-2">
                  <Label className="text-sm font-semibold text-slate-700">Conjunto</Label>
                  <Select value={formData.building_id} onValueChange={(value) => setFormData({...formData, building_id: value})}>
                    <SelectTrigger className="h-11 bg-slate-50 border-slate-200" data-testid="pqrs-building-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {buildings.map(building => (
                        <SelectItem key={building.id} value={building.id}>{building.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label className="text-sm font-semibold text-slate-700">Titulo</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    required
                    className="h-11 bg-slate-50 border-slate-200"
                    placeholder="Resumen breve del caso"
                    data-testid="pqrs-title-input"
                  />
                </div>
                <div className="grid gap-2">
                  <Label className="text-sm font-semibold text-slate-700">Descripcion</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    required
                    rows={4}
                    className="bg-slate-50 border-slate-200 resize-none"
                    placeholder="Detalla tu solicitud..."
                    data-testid="pqrs-description-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label className="text-sm font-semibold text-slate-700">Tipo</Label>
                    <Select value={formData.type} onValueChange={(value) => setFormData({...formData, type: value})}>
                      <SelectTrigger className="h-11 bg-slate-50 border-slate-200" data-testid="pqrs-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="question">Pregunta</SelectItem>
                        <SelectItem value="complaint">Queja</SelectItem>
                        <SelectItem value="claim">Reclamo</SelectItem>
                        <SelectItem value="suggestion">Sugerencia</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label className="text-sm font-semibold text-slate-700">Prioridad</Label>
                    <Select value={formData.priority} onValueChange={(value) => setFormData({...formData, priority: value})}>
                      <SelectTrigger className="h-11 bg-slate-50 border-slate-200" data-testid="pqrs-priority-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Baja</SelectItem>
                        <SelectItem value="medium">Media</SelectItem>
                        <SelectItem value="high">Alta</SelectItem>
                        <SelectItem value="critical">Critica</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="pt-2">
                  <Button type="submit" className="w-full h-12 bg-primary hover:bg-primary/90 text-lg font-semibold rounded-xl" data-testid="pqrs-submit-button">
                    Crear Solicitud
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* PQRS Compact Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="pqrs-grid">
          {sortedPqrsList.length === 0 ? (
            <div className="col-span-full py-16 text-center bg-white rounded-2xl border border-dashed border-slate-200">
              <div className="h-16 w-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="h-8 w-8 text-slate-300" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">No hay solicitudes</h3>
              <p className="text-slate-500 mt-1">Todas las inquietudes han sido resueltas.</p>
            </div>
          ) : (
            sortedPqrsList.map((pqrs) => {
              const typeConfig = TYPE_CONFIGS[pqrs.type] || { label: pqrs.type, icon: FileText, color: 'text-slate-500', bg: 'bg-slate-50' };
              const statusConfig = STATUS_CONFIGS[pqrs.status] || STATUS_CONFIGS.pending;
              const priorityConfig = PRIORITY_CONFIGS[pqrs.priority] || PRIORITY_CONFIGS.medium;
              
              return (
                <Card 
                  key={pqrs.id} 
                  className="group hover:border-blue-200 hover:shadow-md transition-all duration-200 cursor-pointer" 
                  data-testid={`pqrs-item-${pqrs.id}`}
                  onClick={(e) => {
                    // Don't open detail if clicking action buttons
                    if (e.target.closest('[data-action-btn]')) return;
                    setDetailPqrs(pqrs);
                  }}
                >
                  <div className="p-4 h-full flex flex-col">
                    {/* Top row: icon + badges */}
                    <div className="flex items-start justify-between mb-2">
                      <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${typeConfig.bg} ${typeConfig.color} flex-shrink-0`}>
                        <typeConfig.icon className="h-4 w-4" />
                      </div>
                      <div className="flex items-center gap-1.5">
                        {/* AI Urgency Badge - compact */}
                        {!pqrs.ai_procesado ? (
                          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 animate-pulse flex items-center gap-0.5">
                            <Sparkles className="h-2.5 w-2.5" />
                            IA...
                          </span>
                        ) : pqrs.ai_urgencia && (
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${URGENCY_CONFIGS[pqrs.ai_urgencia]?.color || ''}`}>
                            {URGENCY_CONFIGS[pqrs.ai_urgencia]?.label || pqrs.ai_urgencia}
                          </span>
                        )}
                        {/* Status badge - compact */}
                        {isAdmin ? (
                          <div data-action-btn>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  className={`${statusConfig.color} font-medium gap-0.5 h-6 text-[10px] px-1.5`}
                                  data-testid={`status-dropdown-${pqrs.id}`}
                                >
                                  <statusConfig.icon className="h-2.5 w-2.5" />
                                  {statusConfig.label}
                                  <ChevronDown className="h-2.5 w-2.5" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleStatusChange(pqrs.id, 'pending')} className="gap-2" data-testid={`status-pending-${pqrs.id}`}>
                                  <Clock className="h-4 w-4 text-yellow-500" />
                                  Pendiente
                                  {pqrs.status === 'pending' && <Check className="h-4 w-4 ml-auto" />}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleStatusChange(pqrs.id, 'in_progress')} className="gap-2" data-testid={`status-in-progress-${pqrs.id}`}>
                                  <Clock className="h-4 w-4 text-blue-500" />
                                  En progreso
                                  {pqrs.status === 'in_progress' && <Check className="h-4 w-4 ml-auto" />}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleStatusChange(pqrs.id, 'resolved')} className="gap-2 text-green-600" data-testid={`status-resolved-${pqrs.id}`}>
                                  <CheckCircle2 className="h-4 w-4" />
                                  Resuelto
                                  {pqrs.status === 'resolved' && <Check className="h-4 w-4 ml-auto" />}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleStatusChange(pqrs.id, 'closed')} className="gap-2" data-testid={`status-closed-${pqrs.id}`}>
                                  <Check className="h-4 w-4 text-slate-500" />
                                  Cerrar
                                  {pqrs.status === 'closed' && <Check className="h-4 w-4 ml-auto" />}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        ) : (
                          <Badge variant="outline" className={`${statusConfig.color} text-[10px] px-1.5 py-0.5 font-medium`}>
                            {statusConfig.label}
                          </Badge>
                        )}
                      </div>
                    </div>
                    
                    {/* Title + description */}
                    <div className="flex-1 min-h-0">
                      <h3 className="text-sm font-medium text-slate-900 mb-1 line-clamp-1 group-hover:text-blue-600 transition-colors" data-testid={`pqrs-title-${pqrs.id}`}>
                        {pqrs.title}
                      </h3>
                      <p className="text-slate-500 text-xs line-clamp-2 leading-relaxed mb-2">
                        {pqrs.description}
                      </p>
                    </div>

                    {/* Assigned To - compact */}
                    {pqrs.assigned_to_name && (
                      <div className="mb-2 flex items-center gap-1.5 text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-md">
                        <User className="h-3 w-3" />
                        <span className="truncate"><strong>{pqrs.assigned_to_name}</strong></span>
                      </div>
                    )}

                    {/* OpsAgent compact */}
                    {pqrs.ai_procesado && pqrs.ai_respuesta_sugerida && (
                      <div className="mb-2 bg-gradient-to-br from-slate-50 to-blue-50/30 border border-slate-200 rounded-lg p-2.5">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-1 text-[10px] font-semibold text-slate-600">
                            <Bot className="h-3 w-3 text-primary" />
                            OpsAgent
                          </div>
                          {pqrs.ai_confianza && (
                            <span className="text-[9px] font-medium text-slate-400">
                              {Math.round(pqrs.ai_confianza * 100)}%
                            </span>
                          )}
                        </div>
                        <div className="bg-white/80 rounded p-1.5 border border-slate-100">
                          <p className="text-[11px] text-slate-700 line-clamp-2 leading-relaxed">
                            {pqrs.ai_respuesta_sugerida}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Footer: user + priority + actions */}
                    <div className="pt-2 mt-auto border-t border-slate-100">
                      <div className="flex items-center justify-between text-xs mb-2">
                        <span className="font-medium text-slate-600 truncate max-w-[100px]">
                          {pqrs.user_name?.split('@')[0]}
                        </span>
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${priorityConfig.color}`}>
                          {priorityConfig.label}
                        </span>
                      </div>
                      
                      {isAdmin && (
                        <div className="flex gap-1.5" data-action-btn>
                          {pqrs.status !== 'resolved' && pqrs.status !== 'closed' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => { e.stopPropagation(); openAssignDialog(pqrs); }}
                              className="flex-1 gap-1 text-[11px] h-[34px]"
                              data-testid={`assign-btn-${pqrs.id}`}
                            >
                              <UserPlus className="h-3 w-3" />
                              {pqrs.assigned_to ? 'Reasignar' : 'Asignar'}
                            </Button>
                          )}
                          {pqrs.status !== 'resolved' && (
                            <Button
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); handleStatusChange(pqrs.id, 'resolved'); }}
                              className="flex-1 gap-1 text-[11px] h-[34px] bg-green-500 hover:bg-green-600"
                              data-testid={`resolve-btn-${pqrs.id}`}
                            >
                              <CheckCircle2 className="h-3 w-3" />
                              Resolver
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })
          )}
        </div>
      </div>

      {/* Assign Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Asignar PQRS</DialogTitle>
            <DialogDescription>
              Selecciona un administrador o miembro del staff para asignar esta solicitud.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {selectedPqrs && (
              <div className="mb-4 p-3 bg-slate-50 rounded-lg">
                <p className="font-medium text-slate-900">{selectedPqrs.title}</p>
                <p className="text-sm text-slate-500 line-clamp-2">{selectedPqrs.description}</p>
              </div>
            )}
            <Label className="text-sm font-semibold text-slate-700 mb-2 block">Asignar a</Label>
            <Select onValueChange={(value) => handleAssign(selectedPqrs?.id, value)}>
              <SelectTrigger data-testid="assign-user-select">
                <SelectValue placeholder="Selecciona un usuario" />
              </SelectTrigger>
              <SelectContent>
                {staffUsers.map(staffUser => (
                  <SelectItem key={staffUser.id} value={staffUser.id}>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-slate-400" />
                      <span>{staffUser.name}</span>
                      <Badge variant="outline" className="text-xs ml-2">
                        {staffUser.role === 'admin' ? 'Admin' : staffUser.role === 'super_admin' ? 'Super Admin' : 'Staff'}
                      </Badge>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Slide-Over */}
      {detailPqrs && (
        <PQRSDetailPanel
          pqrs={detailPqrs}
          isAdmin={isAdmin}
          onClose={closeDetail}
          onStatusChange={handleStatusChange}
          onAssign={(pqrs) => { closeDetail(); openAssignDialog(pqrs); }}
          staffUsers={staffUsers}
          onPqrsUpdated={fetchPQRS}
        />
      )}
    </DashboardLayout>
  );
};

export default PQRSPage;
